import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Plus, Trash, PencilSimple, Check, X, MagnifyingGlass, FilePdf, FileXls, Tag } from "@phosphor-icons/react";
import { usePeriodFilter } from "./PeriodFilter";

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

const downloadFile = (url, token) => {
  const a = document.createElement("a");
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => r.blob())
    .then(blob => { a.href = URL.createObjectURL(blob); a.download = ""; a.click(); });
};

export function IncomesPanel({ year, month }) {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [showNewCat, setShowNewCat] = useState(false);
  const [form, setForm] = useState({ client_name: "", amount: "", description: "", category: "Venta", date: new Date().toISOString().slice(0, 10), quotation_id: "", quotation_number: "" });

  const load = useCallback(async () => {
    try {
      const params = { year };
      if (month) params.month = month;
      const [incRes, catRes] = await Promise.all([
        axios.get(`${API}/admin/incomes`, { params, headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/income-categories`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setItems(incRes.data);
      setCategories(catRes.data);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [year, month, token]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.client_name || !form.amount || !form.date) { toast.error("Completa los campos requeridos"); return; }
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (editId) {
        await axios.put(`${API}/admin/incomes/${editId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Ingreso actualizado");
      } else {
        await axios.post(`${API}/admin/incomes`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Ingreso registrado");
      }
      resetForm(); load();
    } catch { toast.error("Error al guardar"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este ingreso?")) return;
    try { await axios.delete(`${API}/admin/incomes/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setForm({ client_name: item.client_name, amount: item.amount, description: item.description, category: item.category || "Venta", date: item.date, quotation_id: item.quotation_id || "", quotation_number: item.quotation_number || "" });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditId(null); setShowForm(false);
    setForm({ client_name: "", amount: "", description: "", category: "Venta", date: new Date().toISOString().slice(0, 10), quotation_id: "", quotation_number: "" });
  };

  const handleAddCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await axios.post(`${API}/admin/income-categories`, { name: newCat.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setForm(p => ({ ...p, category: newCat.trim() }));
      setNewCat(""); setShowNewCat(false); load();
      toast.success("Categoria agregada");
    } catch { toast.error("Error"); }
  };

  const searchFiltered = items.filter(i => i.client_name?.toLowerCase().includes(search.toLowerCase()) || i.description?.toLowerCase().includes(search.toLowerCase()));
  const { filtered, PeriodFilter } = usePeriodFilter(searchFiltered, year);
  const total = filtered.reduce((s, i) => s + (i.amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="incomes-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." className="pl-9 pr-3 py-2 border rounded-lg text-sm w-64" data-testid="income-search" />
          </div>
          <span className="text-sm font-semibold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg">Total: {fmt(total)}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { resetForm(); setShowForm(true); }} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center gap-1.5" data-testid="income-add-btn">
            <Plus size={16} /> Nuevo Ingreso
          </button>
          <button onClick={() => downloadFile(`${API}/admin/incomes/pdf?${year ? `year=${year}` : ""}${month ? `&month=${month}` : ""}`, token)} className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 flex items-center gap-1.5" data-testid="income-pdf-btn">
            <FilePdf size={16} /> PDF
          </button>
          <button onClick={() => downloadFile(`${API}/admin/incomes/excel?${year ? `year=${year}` : ""}${month ? `&month=${month}` : ""}`, token)} className="px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 flex items-center gap-1.5" data-testid="income-excel-btn">
            <FileXls size={16} /> Excel
          </button>
        </div>
      </div>

      <PeriodFilter />

      {showForm && (
        <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="income-form">
          <h3 className="font-semibold text-slate-700">{editId ? "Editar Ingreso" : "Nuevo Ingreso"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Cliente *</label>
              <input value={form.client_name} onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="income-client" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Monto *</label>
              <input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="income-amount" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Fecha *</label>
              <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="income-date" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Categoria</label>
              <div className="flex gap-1 mt-0.5">
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="flex-1 px-3 py-2 border rounded-lg text-sm" data-testid="income-category-select">
                  {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <button type="button" onClick={() => setShowNewCat(!showNewCat)} className="px-2 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100" title="Agregar categoria" data-testid="income-add-cat-btn">
                  <Tag size={16} />
                </button>
              </div>
              {showNewCat && (
                <div className="flex gap-1 mt-1">
                  <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Nueva categoria..." className="flex-1 px-2 py-1.5 border rounded-lg text-xs" onKeyDown={e => e.key === "Enter" && handleAddCategory()} data-testid="income-new-cat-input" />
                  <button onClick={handleAddCategory} className="px-2 py-1 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700" data-testid="income-new-cat-save">OK</button>
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Descripcion</label>
              <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="income-desc" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">No. Cotizacion (opcional)</label>
              <input value={form.quotation_number} onChange={e => setForm(p => ({ ...p, quotation_number: e.target.value }))} placeholder="COT-XXXX" className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1" data-testid="income-save-btn">
              <Check size={16} /> {editId ? "Actualizar" : "Guardar"}
            </button>
            <button onClick={resetForm} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200 flex items-center gap-1">
              <X size={16} /> Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Fecha</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Cliente</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Descripcion</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Categoria</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Monto</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map(item => (
              <tr key={item.id} className="hover:bg-slate-50" data-testid={`income-row-${item.id}`}>
                <td className="px-4 py-3 text-slate-600">{item.date}</td>
                <td className="px-4 py-3 font-medium text-slate-700">{item.client_name}</td>
                <td className="px-4 py-3 text-slate-500">{item.description}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">{item.category}</span></td>
                <td className="px-4 py-3 text-right font-semibold text-emerald-700">{fmt(item.amount)}</td>
                <td className="px-4 py-3 text-center">
                  <div className="flex justify-center gap-1">
                    <button onClick={() => startEdit(item)} className="p-1.5 hover:bg-blue-50 rounded text-blue-600" data-testid={`income-edit-${item.id}`}><PencilSimple size={14} /></button>
                    {isAdmin && <button onClick={() => handleDelete(item.id)} className="p-1.5 hover:bg-red-50 rounded text-red-500" data-testid={`income-del-${item.id}`}><Trash size={14} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No hay ingresos registrados</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
